import { StepLayout } from '@/components/NewGeneralDialog/StepLayout';
import { WizardAnimationWrapper } from '@/components/WizardAnimationWrapper';
import { useGetCompanyRiskKyc } from '@/hooks/useGetCompanyRiskKyc';
import { useGetOnfidoCheck } from '@/hooks/useGetOnfidoCheck';
import { Documents } from '@/types/company';

import {
  Box,
  Button,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { getCountryName } from '@treyd-io/core/utils/country';
import { Col } from '@treyd-io/treyd-ui/components/Grid/Col';
import { Row } from '@treyd-io/treyd-ui/components/Grid/Row';
import ArrowRightIcon from '@treyd-io/treyd-ui/icons/ArrowRightIcon';
import { format, isValid } from 'date-fns';
import { Form, Formik, FormikProps } from 'formik';
import { filter, find, join, toString } from 'lodash';
import { useSession } from 'next-auth/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

interface DocumentOverviewProps {
  onMovingForward: (values: Partial<Documents.DocumentType['data']>) => void;
  formData: Partial<Documents.DocumentType['data']>;
  next?: boolean;
}

export const DocumentOverview = ({
  onMovingForward,
  formData,
  next,
}: DocumentOverviewProps) => {
  const { t } = useTranslation();
  const [isBirthdatePickerOpen, setIsBirthdatePickerOpen] = useState(false);
  const { data: session } = useSession({ required: true });
  const userId = session?.user?.id;
  const companyId = session?.user?.company_id;
  const userEmail = toString(session?.user?.email);
  const { data: onfidoData } = useGetOnfidoCheck(userId);
  const { data: companyRiskKycData } = useGetCompanyRiskKyc(companyId);
  const kycData =
    companyRiskKycData?.orders_companies_by_pk?.core_company?.risk_kycs?.[0]
      ?.data;
  const companySigners = kycData?.company_signers || [];

  const currentSigner = find(companySigners, {
    email: userEmail,
  });

  const currentSignerAddress = currentSigner?.address;
  const currentSignerCity = currentSigner?.city;
  const currentSignerCountry = getCountryName(
    currentSigner?.country_of_residence || ''
  );
  const currentSignerFullAddress =
    currentSigner &&
    join(
      filter(
        [currentSignerAddress, currentSignerCity, currentSignerCountry],
        Boolean
      ),
      ', '
    );

  const onfidoCheckReportProperties =
    onfidoData?.lager_treyd_auth_onfidocheck?.[0]?.reports_payload?.reports?.[0]
      ?.properties;
  const onfidoDateOfBirth = toString(
    onfidoCheckReportProperties?.date_of_birth
  );
  const isValidDateOfBirth = isValid(new Date(onfidoDateOfBirth));
  const dateOfBirth = isValidDateOfBirth
    ? format(new Date(toString(onfidoDateOfBirth)), 'yyyy-MM-dd')
    : null;
  const initialValues = {
    signer_birthdate: formData?.Signer_Birthdate || dateOfBirth || null,
    signer_address: formData?.Signer_Address || currentSignerFullAddress || '',
  };

  const validationSchema = Yup.object({
    signer_birthdate: Yup.date().required(t('birthdate_is_required')),
    signer_address: Yup.string().required(t('address_is_required')),
  });

  const handleSubmit = (values: typeof initialValues) => {
    onMovingForward({
      Signer_Birthdate: values.signer_birthdate,
      Signer_Address: values.signer_address,
    });
  };

  return (
    <WizardAnimationWrapper firstStep next={next}>
      <StepLayout>
        <Stack width="100%" gap={3}>
          <Stack gap={1}>
            <Typography variant="h4">
              {t('statement_of_assets_liabilities')}
            </Typography>
            <Stack>
              <Typography variant="body1">
                {t(
                  'we_require_some_information_from_you_before_we_can_proceed_with_setting_your_limit_in_the_following_steps_youll_be_asked_to_complete_your_statement_of_assets_and_liabilities_as_part_of_treyds_assessment_for_a_personal_guarantee_the_process_is_simple_and_includes_a_few_short_sections'
                )}
              </Typography>
              <List
                disablePadding
                sx={{
                  paddingInline: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  listStyleType: 'disc',
                }}>
                <ListItem disablePadding sx={{ display: 'list-item' }}>
                  {t('assets')}
                </ListItem>
                <ListItem disablePadding sx={{ display: 'list-item' }}>
                  {t('liabilities')}
                </ListItem>
                <ListItem disablePadding sx={{ display: 'list-item' }}>
                  {t('income_information')}
                </ListItem>
              </List>
              <Typography variant="body1">
                {t(
                  'this_allows_us_to_understand_your_financial_position_and_conduct_a_responsible_credit_assessment_it_only_takes_a_minute_and_brings_you_closer_to_reaching_your_financial_goals'
                )}
              </Typography>
              <Typography variant="body1">
                {t(
                  'youll_get_a_chance_to_review_and_confirm_all_the_details_in_the_last_step'
                )}
              </Typography>
            </Stack>
          </Stack>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize>
            {(formikProps: FormikProps<typeof initialValues>) => (
              <Form>
                <Stack gap={3}>
                  <Box
                    sx={{
                      p: 3,
                      border: 1,
                      borderColor: 'other.outlined_border',
                      borderRadius: 4,
                      boxShadow: 'none',
                      backgroundColor: 'background.default',
                    }}>
                    <Stack gap={2}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t('your_personal_details')}
                      </Typography>

                      <Row spacing={3}>
                        <Col xs={12} md={6}>
                          <DatePicker
                            sx={{ width: '100%' }}
                            label={t('date_of_birth')}
                            disableFuture
                            value={
                              formikProps.values.signer_birthdate
                                ? new Date(formikProps.values.signer_birthdate)
                                : null
                            }
                            onChange={(date, context) => {
                              formikProps.setFieldValue(
                                'signer_birthdate',
                                isValid(date) &&
                                  (!context.validationError ||
                                    context.validationError.length === 0)
                                  ? format(date as Date, 'yyyy-MM-dd')
                                  : null,
                                true
                              );
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                name: 'signer_birthdate',
                                onClick: () => setIsBirthdatePickerOpen(true),
                                onBlur: formikProps.handleBlur,
                                error: !!(
                                  formikProps.touched.signer_birthdate &&
                                  formikProps.errors.signer_birthdate
                                ),
                                helperText:
                                  formikProps.touched.signer_birthdate &&
                                  formikProps.errors.signer_birthdate,
                                variant: 'outlined',
                              },
                            }}
                            onClose={() => setIsBirthdatePickerOpen(false)}
                            open={isBirthdatePickerOpen}
                          />
                        </Col>
                        <Col xs={12} md={6}>
                          <TextField
                            fullWidth
                            name="signer_address"
                            label={t('address')}
                            value={formikProps.values.signer_address}
                            onChange={formikProps.handleChange}
                            onBlur={formikProps.handleBlur}
                            error={
                              !!(
                                formikProps.touched.signer_address &&
                                formikProps.errors.signer_address
                              )
                            }
                            helperText={
                              formikProps.touched.signer_address &&
                              formikProps.errors.signer_address
                            }
                            variant="outlined"
                          />
                        </Col>
                      </Row>
                    </Stack>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    type="submit"
                    disabled={!formikProps.isValid}
                    endIcon={<ArrowRightIcon />}
                    sx={{ alignSelf: { sm: 'flex-start' }, marginTop: 0.5 }}>
                    {t('continue')}
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </StepLayout>
    </WizardAnimationWrapper>
  );
};
